/**
 * Plain-language copy for watch/alert toggles.
 *
 * Kept pure and separate from the mutation so it can be unit tested: this copy is
 * the only thing that tells a buyer what a toggle just did, and it must never leak
 * a raw internal watch_type value (snake_case like 'saved_search') to the screen.

 */

export type WatchType = 'listing' | 'supplier' | 'search' | 'menu';

/** Human noun per watch type. Customer-facing wording is always "listing", never "lot". */
export const WATCH_NOUN: Record<WatchType, string> = {
  listing: 'listing',
  supplier: 'supplier',
  search: 'search',
  menu: 'menu',
};

export function watchNoun(type: WatchType | string | null | undefined): string {
  if (!type) return 'item';
  return WATCH_NOUN[type as WatchType] ?? 'item';
}

export interface WatchToggleCopy {
  title: string;
  description?: string;
}

/**
 * Copy for the toast after a watch is added or removed.
 * `added: true` also carries a description promising the reward, which is what
 * makes the trigger worth keeping on.
 */
export function watchToggleCopy(type: WatchType, added: boolean): WatchToggleCopy {
  const noun = watchNoun(type);

  if (!added) {
    if (type === 'supplier') return { title: 'Unfollowed supplier' };
    if (type === 'menu') return { title: 'Alerts off for this menu' };
    return { title: `Stopped watching this ${noun}` };
  }

  if (type === 'supplier') {
    return {
      title: 'Following supplier',
      description: 'You will hear about everything they share with you.',
    };
  }
  if (type === 'menu') {
    return {
      title: 'Alerts on for this menu',
      description: 'You will hear when this menu gets new or refreshed inventory.',
    };
  }
  return {
    title: `Watching this ${noun}`,
    description: 'Price changes and sold-out alerts will reach you.',
  };
}
/**
 * The only route a watched listing may open: the buyer menu view under the
 * MENU's supplier. Never `/listings/:id` (the owner inventory route).
 */
export const watchedListingPath = (l: {
  supplier_company_id: string | null;
  menu_id: string | null;
  listing_id: string;
}) => `/menu/${l.supplier_company_id}/${l.menu_id}/listing/${l.listing_id}`;
